/*
 * Purpose: Autoblocks Stripe price catalog (Basic/Plus · US/UK · month/year)
 * Last modified: 2026-07-22 | Agent
 * Completeness: 95
 */
import type { PricingPlanId } from '@/lib/pricingPlans';
import { getStripePricingRegion, type StripePricingRegion } from '@/lib/stripeConfig';

export type StripeBillingInterval = 'month' | 'year';

export type StripeCheckoutPrice = {
  priceId: string;
  planId: PricingPlanId;
  interval: StripeBillingInterval;
  currency: 'usd' | 'gbp';
  /** Minor units (cents / pence) */
  unitAmount: number;
  label: string;
};

/** Public Stripe Price IDs — safe in the client; sessions are created server-side. */
export const STRIPE_CHECKOUT_PRICES: StripeCheckoutPrice[] = [
  {
    priceId: 'price_1Tf8eNBsz6Xe3f1UgauMTngw',
    planId: 'basic',
    interval: 'month',
    currency: 'usd',
    unitAmount: 1200,
    label: '$12/mo',
  },
  {
    priceId: 'price_1Tf8iOBsz6Xe3f1Uzims9k4Z',
    planId: 'basic',
    interval: 'year',
    currency: 'usd',
    unitAmount: 12000,
    label: '$120/yr',
  },
  {
    priceId: 'price_1Tf8gyBsz6Xe3f1UCclV93Lp',
    planId: 'plus',
    interval: 'month',
    currency: 'usd',
    unitAmount: 2400,
    label: '$24/mo',
  },
  {
    priceId: 'price_1Tf8gyBsz6Xe3f1UzJfUJkpe',
    planId: 'plus',
    interval: 'year',
    currency: 'usd',
    unitAmount: 24000,
    label: '$240/yr',
  },
  {
    priceId: 'price_1TnM01Bsz6Xe3f1UTtx5mcEz',
    planId: 'basic',
    interval: 'month',
    currency: 'gbp',
    unitAmount: 800,
    label: '£8/mo',
  },
  {
    priceId: 'price_1TnM0IBsz6Xe3f1UwHTYZsRD',
    planId: 'basic',
    interval: 'year',
    currency: 'gbp',
    unitAmount: 8000,
    label: '£80/yr',
  },
  {
    priceId: 'price_1TnLwFBsz6Xe3f1UUfnVuWKy',
    planId: 'plus',
    interval: 'month',
    currency: 'gbp',
    unitAmount: 1000,
    label: '£10/mo',
  },
  {
    priceId: 'price_1TnLyTBsz6Xe3f1UFJyc8nov',
    planId: 'plus',
    interval: 'year',
    currency: 'gbp',
    unitAmount: 10000,
    label: '£100/yr',
  },
];

export function resolveCheckoutCurrency(region?: StripePricingRegion): 'usd' | 'gbp' {
  const r = region ?? getStripePricingRegion();
  return r === 'uk' ? 'gbp' : 'usd';
}

export function getCheckoutPrice(
  planId: PricingPlanId,
  interval: StripeBillingInterval,
  currency?: 'usd' | 'gbp'
): StripeCheckoutPrice | undefined {
  const cur = currency ?? resolveCheckoutCurrency();
  return STRIPE_CHECKOUT_PRICES.find(
    (p) => p.planId === planId && p.interval === interval && p.currency === cur
  );
}

export function formatCheckoutAmount(price: StripeCheckoutPrice): string {
  return price.label;
}
